import React from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Icon, 
  Heading, 
  Grid,
  GridItem,
  VStack,
  HStack,
  Circle,
  Divider,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Users, 
  Server, 
  Code,
  Briefcase,
  PieChart,
  BarChart,
  AlertCircle
} from 'lucide-react';
import NeuralBackground from '../../components/NeuralBackground';
import SlideContainer from '../../components/SlideContainer';
import { MethodistSlide } from '../../components/base/MethodistSlide';

// Budget category component
const BudgetCategory: React.FC<{
  title: string;
  amount: number;
  percentage: number;
  icon: React.FC;
  color: string;
  index: number;
}> = ({ title, amount, percentage, icon, color, index }) => {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index, duration: 0.5 }}
      style={{ width: "100%" }}
    >
      <Flex 
        bg="white" 
        p={4} 
        borderRadius="md" 
        boxShadow="sm" 
        borderWidth="1px" 
        borderColor="gray.200"
        align="center"
        position="relative"
        overflow="hidden"
      >
        <Box 
          position="absolute" 
          top={0} 
          left={0} 
          bottom={0} 
          width={`${percentage}%`} 
          bg={`${color}15`} 
          zIndex={0} 
        />
        
        <Circle size="40px" bg={`${color}20`} color={color} mr={4} zIndex={1} flexShrink={0}>
          <Icon as={icon} boxSize={5} />
        </Circle>
        
        <Box flex="1" zIndex={1}>
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            {title}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {percentage}% of total budget
          </Text>
        </Box>
        
        <Text fontWeight="bold" fontSize="md" color={color} zIndex={1}>
          {formattedAmount}
        </Text>
      </Flex>
    </motion.div>
  );
};

const BudgetSlide: React.FC = () => {
  // Colors
  const methodistBlue = "#0066CC";
  const methodistGreen = "#4A8B71";
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Budget data
  const totalBudget = 5000;
  const budgetCategories = [
    {
      title: "Development Team",
      amount: 2200,
      percentage: 44,
      icon: Code,
      color: methodistBlue
    },
    {
      title: "Infrastructure & Hosting",
      amount: 800,
      percentage: 16,
      icon: Server,
      color: "#805AD5" // Purple
    },
    {
      title: "AI & Data Science",
      amount: 1000,
      percentage: 20,
      icon: PieChart,
      color: "#DD6B20" // Orange
    },
    {
      title: "Training & Change Management",
      amount: 500,
      percentage: 10,
      icon: Users,
      color: methodistGreen
    },
    {
      title: "Project Management",
      amount: 300,
      percentage: 6,
      icon: Briefcase,
      color: "#E53E3E" // Red
    },
    {
      title: "Contingency",
      amount: 200,
      percentage: 4,
      icon: DollarSign,
      color: "#718096" // Gray
    }
  ];
  
  // Budget breakdown by phase
  const phaseBreakdown = [
    { phase: "Phase 1: Foundation", amount: 1500, percentage: "30%" },
    { phase: "Phase 2: Development", amount: 2000, percentage: "40%" },
    { phase: "Phase 3: Testing", amount: 750, percentage: "15%" },
    { phase: "Phase 4: Deployment", amount: 750, percentage: "15%" }
  ];
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Market comparison value
  const marketComparisonValue = 100000;
  
  return (
    <MethodistSlide title="Project Budget">
      <SlideContainer>
        <Box position="relative" height="100%" overflow="hidden">
          <Box position="absolute" top={0} right={0} bottom={0} left={0} zIndex={-1} opacity={0.05}>
            <NeuralBackground />
          </Box>
          
          <Flex direction="column" height="100%" p={6} maxHeight="100%" overflowY="auto"> 
            {/* Total budget */}
            <Box 
              bg="blue.50" 
              p={4} 
              borderRadius="md" 
              borderWidth="1px" 
              borderColor="blue.100"
              mb={6}
            >
              <Flex align="center" justify="space-between" flexWrap="wrap">
                <Box>
                  <Text fontSize="sm" color="blue.700">Total Project Budget</Text>
                  <Heading size="lg" color={methodistBlue}>
                    {formatCurrency(totalBudget)}
                  </Heading>
                </Box>
                <HStack spacing={6} divider={<Divider orientation="vertical" height="40px" />}>
                  <Box textAlign="center">
                    <Text fontSize="xs" color="blue.700">Development</Text>
                    <Text fontSize="lg" fontWeight="bold" color={methodistBlue}>
                      {formatCurrency(totalBudget * 0.85)}
                    </Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="xs" color="blue.700">Annual Maintenance</Text>
                    <Text fontSize="lg" fontWeight="bold" color={methodistBlue}>
                      {formatCurrency(totalBudget * 0.15)}
                    </Text>
                  </Box>
                </HStack>
              </Flex>
              
              {/* Market comparison */}
              <Box mt={3} p={2} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.100">
                <Flex align="center">
                  <Icon as={AlertCircle} color="orange.500" mr={2} />
                  <Text fontSize="sm" color="orange.700">
                    <strong>Market Value:</strong> Similar platforms would cost {formatCurrency(marketComparisonValue)}+ to develop commercially
                  </Text>
                </Flex>
              </Box>
            </Box>
            
            {/* Main content */}
            <Grid templateColumns={{base: "1fr", md: "3fr 2fr"}} gap={6} flex="1" maxHeight="calc(100% - 160px)" overflowY="hidden"> 
              {/* Left side - Budget Categories */}
              <GridItem overflowY="auto"> 
                <VStack spacing={4} align="stretch" height="100%"> 
                  <Heading size="sm" mb={1} color={methodistBlue}>
                    Budget Allocation by Category
                  </Heading>
                  
                  <VStack spacing={3} align="stretch">
                    {budgetCategories.map((category, index) => (
                      <BudgetCategory
                        key={index}
                        title={category.title}
                        amount={category.amount}
                        percentage={category.percentage}
                        icon={category.icon}
                        color={category.color}
                        index={index}
                      />
                    ))}
                  </VStack>
                </VStack>
              </GridItem>
              
              {/* Right side - Phase Breakdown */}
              <GridItem overflowY="auto"> 
                <VStack spacing={4} align="stretch" height="100%">
                  <Box bg={bgColor} borderRadius="lg" boxShadow="md" p={4} borderWidth="1px" borderColor={borderColor}>
                    <Heading size="sm" mb={4} color={methodistBlue}> 
                      Budget by Implementation Phase
                    </Heading>
                    
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Phase</Th>
                          <Th isNumeric>Amount</Th>
                          <Th isNumeric>%</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {phaseBreakdown.map((phase, index) => (
                          <Tr key={index}>
                            <Td fontSize="xs">{phase.phase}</Td>
                            <Td isNumeric fontSize="xs" fontWeight="medium">{formatCurrency(phase.amount)}</Td>
                            <Td isNumeric fontSize="xs">{phase.percentage}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  
                  <Box bg="green.50" p={4} borderRadius="md" borderWidth="1px" borderColor="green.100">
                    <Heading size="xs" mb={3} color="green.700">
                      Budget Highlights
                    </Heading>
                    
                    <VStack spacing={3} align="stretch">
                      <HStack spacing={3}>
                        <Circle size="24px" bg={`${methodistGreen}20`} color={methodistGreen} flexShrink={0}>
                          <Icon as={BarChart} boxSize={3} />
                        </Circle>
                        <Text fontSize="xs" color="green.700">
                          64% of budget allocated to technical development and AI capabilities
                        </Text>
                      </HStack>
                      
                      <HStack spacing={3}>
                        <Circle size="24px" bg={`${methodistGreen}20`} color={methodistGreen} flexShrink={0}>
                          <Icon as={BarChart} boxSize={3} />
                        </Circle>
                        <Text fontSize="xs" color="green.700">
                          10% dedicated to training and change management to ensure adoption
                        </Text>
                      </HStack>
                      
                      <HStack spacing={3}>
                        <Circle size="24px" bg={`${methodistGreen}20`} color={methodistGreen} flexShrink={0}>
                          <Icon as={BarChart} boxSize={3} />
                        </Circle>
                        <Text fontSize="xs" color="green.700">
                          4% contingency buffer to address unforeseen challenges
                        </Text>
                      </HStack>
                      
                      <HStack spacing={3}>
                        <Circle size="24px" bg={`${methodistGreen}20`} color={methodistGreen} flexShrink={0}>
                          <Icon as={BarChart} boxSize={3} />
                        </Circle>
                        <Text fontSize="xs" color="green.700">
                          Annual maintenance costs estimated at 15% of initial development
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                </VStack>
              </GridItem>
            </Grid>
            
            {/* Footer note */}
            <Box mt={4} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.100"> 
              <Text fontSize="sm" color="blue.700">
                <Icon as={DollarSign} verticalAlign="middle" mr={1} />
                Budget represents seed funding request for initial development and pilot. Commercial equivalents would cost 20x more,
                but our in-house expertise and existing infrastructure significantly reduce costs.
              </Text>
            </Box>
          </Flex>
        </Box>
      </SlideContainer>
    </MethodistSlide>
  );
};

export default BudgetSlide;
